/**
 * Phone-number domain logic for the portal.
 *
 * The problem this solves: an agent at a counter types the number the customer
 * reads out. Nobody reads out `+252`. They say "six one, two three four…", or
 * they read a number off a SIM card as `0612345678`, or they paste
 * `+252 61 234 5678` out of WhatsApp. Every one of those is the same customer,
 * and until now each surface forced the agent to normalise it by hand — with a
 * placeholder as the only instruction, and a silent search miss as the penalty
 * for getting it wrong.
 *
 * So parsing here is deliberately forgiving on input and strict on output:
 * anything a human might plausibly type resolves to one canonical E.164 string,
 * or to `null` when it genuinely is not a number yet.
 *
 * This module is pure — no React, no network. The UI lives in
 * `@/components/ui/phone-input`.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Country catalog
// ─────────────────────────────────────────────────────────────────────────────

export interface Country {
  /** ISO 3166-1 alpha-2. Stable identity for the country, used as the key. */
  iso2: string;
  /** Country calling code, digits only, no `+`. */
  dialCode: string;
  /** English display name. */
  name: string;
  /** Regional-indicator emoji, used as the visual anchor in the picker. */
  flag: string;
  /**
   * Valid subscriber-number lengths (country code and trunk prefix excluded).
   * Several entries where the country genuinely varies; a number matching none
   * of them is treated as incomplete rather than wrong, so the field never
   * shouts at someone who is still typing.
   */
  lengths: readonly number[];
  /** Display grouping for the subscriber number, e.g. `[2, 3, 4]` → `61 234 5678`. */
  groups: readonly number[];
  /**
   * Leading digits a *mobile* subscriber number must start with, where the
   * country has a clean mobile/landline split worth enforcing. Omitted where it
   * does not, because rejecting a valid number is far worse than accepting a
   * landline the backend will reject anyway with a real error.
   */
  mobileLeading?: RegExp;
  /** Sample subscriber number, shown as the placeholder. */
  example: string;
}

/**
 * Somalia first — it is the market, and the picker should never make the
 * common case hunt. The rest is the Horn, the Gulf, and the diaspora
 * corridors, which between them cover every number this portal realistically
 * sees.
 *
 * `+1` is listed once as "United States / Canada": the two are genuinely
 * indistinguishable by dial code, and offering both would present a choice
 * with no correct answer.
 */
export const COUNTRIES: readonly Country[] = [
  // ── Horn of Africa ────────────────────────────────────────────────────────
  {
    iso2: 'SO',
    dialCode: '252',
    name: 'Somalia',
    flag: '🇸🇴',
    lengths: [9],
    groups: [2, 3, 4],
    mobileLeading: /^[6-9]/,
    // Leads with `63` (Telesom — see SOMALI_NETWORKS below), the prefix this
    // portal onboards on, so the ghost text looks like the numbers an agent
    // actually types rather than a different operator's range.
    example: '63 234 5678',
  },
  {
    iso2: 'ET',
    dialCode: '251',
    name: 'Ethiopia',
    flag: '🇪🇹',
    lengths: [9],
    groups: [2, 3, 4],
    example: '91 234 5678',
  },
  {
    iso2: 'KE',
    dialCode: '254',
    name: 'Kenya',
    flag: '🇰🇪',
    lengths: [9],
    groups: [3, 3, 3],
    example: '712 345 678',
  },
  {
    iso2: 'DJ',
    dialCode: '253',
    name: 'Djibouti',
    flag: '🇩🇯',
    lengths: [8],
    groups: [2, 2, 2, 2],
    example: '77 83 10 01',
  },
  {
    iso2: 'ER',
    dialCode: '291',
    name: 'Eritrea',
    flag: '🇪🇷',
    lengths: [7],
    groups: [1, 3, 3],
    example: '7 123 456',
  },
  {
    iso2: 'UG',
    dialCode: '256',
    name: 'Uganda',
    flag: '🇺🇬',
    lengths: [9],
    groups: [3, 3, 3],
    example: '712 345 678',
  },
  {
    iso2: 'TZ',
    dialCode: '255',
    name: 'Tanzania',
    flag: '🇹🇿',
    lengths: [9],
    groups: [3, 3, 3],
    example: '712 345 678',
  },

  // ── Gulf & Middle East ────────────────────────────────────────────────────
  {
    iso2: 'YE',
    dialCode: '967',
    name: 'Yemen',
    flag: '🇾🇪',
    lengths: [9],
    groups: [3, 3, 3],
    example: '712 345 678',
  },
  {
    iso2: 'AE',
    dialCode: '971',
    name: 'United Arab Emirates',
    flag: '🇦🇪',
    lengths: [9],
    groups: [2, 3, 4],
    example: '50 123 4567',
  },
  {
    iso2: 'SA',
    dialCode: '966',
    name: 'Saudi Arabia',
    flag: '🇸🇦',
    lengths: [9],
    groups: [2, 3, 4],
    example: '50 123 4567',
  },
  {
    iso2: 'QA',
    dialCode: '974',
    name: 'Qatar',
    flag: '🇶🇦',
    lengths: [8],
    groups: [4, 4],
    example: '3312 3456',
  },
  {
    iso2: 'OM',
    dialCode: '968',
    name: 'Oman',
    flag: '🇴🇲',
    lengths: [8],
    groups: [4, 4],
    example: '9212 3456',
  },
  {
    iso2: 'KW',
    dialCode: '965',
    name: 'Kuwait',
    flag: '🇰🇼',
    lengths: [8],
    groups: [4, 4],
    example: '5012 3456',
  },
  {
    iso2: 'BH',
    dialCode: '973',
    name: 'Bahrain',
    flag: '🇧🇭',
    lengths: [8],
    groups: [4, 4],
    example: '3600 1234',
  },
  {
    iso2: 'EG',
    dialCode: '20',
    name: 'Egypt',
    flag: '🇪🇬',
    lengths: [10],
    groups: [3, 3, 4],
    example: '100 123 4567',
  },
  {
    iso2: 'TR',
    dialCode: '90',
    name: 'Türkiye',
    flag: '🇹🇷',
    lengths: [10],
    groups: [3, 3, 4],
    example: '501 234 5678',
  },

  // ── Diaspora corridors ────────────────────────────────────────────────────
  {
    iso2: 'GB',
    dialCode: '44',
    name: 'United Kingdom',
    flag: '🇬🇧',
    lengths: [10],
    groups: [4, 6],
    example: '7400 123456',
  },
  {
    iso2: 'US',
    dialCode: '1',
    name: 'United States / Canada',
    flag: '🇺🇸',
    lengths: [10],
    groups: [3, 3, 4],
    example: '202 555 0123',
  },
  {
    iso2: 'SE',
    dialCode: '46',
    name: 'Sweden',
    flag: '🇸🇪',
    lengths: [7, 8, 9],
    groups: [2, 3, 4],
    example: '70 123 4567',
  },
  {
    iso2: 'NO',
    dialCode: '47',
    name: 'Norway',
    flag: '🇳🇴',
    lengths: [8],
    groups: [3, 2, 3],
    example: '406 12 345',
  },
  {
    iso2: 'DK',
    dialCode: '45',
    name: 'Denmark',
    flag: '🇩🇰',
    lengths: [8],
    groups: [2, 2, 2, 2],
    example: '32 12 34 56',
  },
  {
    iso2: 'FI',
    dialCode: '358',
    name: 'Finland',
    flag: '🇫🇮',
    lengths: [9, 10],
    groups: [2, 3, 4],
    example: '41 234 5678',
  },
  {
    iso2: 'NL',
    dialCode: '31',
    name: 'Netherlands',
    flag: '🇳🇱',
    lengths: [9],
    groups: [1, 4, 4],
    example: '6 1234 5678',
  },
  {
    iso2: 'DE',
    dialCode: '49',
    name: 'Germany',
    flag: '🇩🇪',
    lengths: [10, 11],
    groups: [3, 4, 4],
    example: '151 2345 6789',
  },
  {
    iso2: 'IT',
    dialCode: '39',
    name: 'Italy',
    flag: '🇮🇹',
    lengths: [9, 10],
    groups: [3, 3, 4],
    example: '312 345 6789',
  },
  {
    iso2: 'ZA',
    dialCode: '27',
    name: 'South Africa',
    flag: '🇿🇦',
    lengths: [9],
    groups: [2, 3, 4],
    example: '71 234 5678',
  },
  {
    iso2: 'IN',
    dialCode: '91',
    name: 'India',
    flag: '🇮🇳',
    lengths: [10],
    groups: [5, 5],
    example: '98765 43210',
  },
  {
    iso2: 'CN',
    dialCode: '86',
    name: 'China',
    flag: '🇨🇳',
    lengths: [11],
    groups: [3, 4, 4],
    example: '131 2345 6789',
  },
  {
    iso2: 'MY',
    dialCode: '60',
    name: 'Malaysia',
    flag: '🇲🇾',
    lengths: [9, 10],
    groups: [2, 3, 4],
    example: '12 345 6789',
  },
];

/** The market. Every field defaults here, and the picker pins it to the top. */
export const DEFAULT_COUNTRY_ISO2 = 'SO';

const BY_ISO2 = new Map(COUNTRIES.map((c) => [c.iso2, c]));

/** Dial codes longest-first, so `+252` wins over a hypothetical `+25`. */
const BY_DIAL_LENGTH: readonly Country[] = [...COUNTRIES].sort(
  (a, b) => b.dialCode.length - a.dialCode.length
);

const DEFAULT_COUNTRY = COUNTRIES.find((c) => c.iso2 === DEFAULT_COUNTRY_ISO2)!;

/** Look up a country, falling back to the market default for anything unknown. */
export function getCountry(iso2: string | null | undefined): Country {
  if (!iso2) return DEFAULT_COUNTRY;
  return BY_ISO2.get(iso2.toUpperCase()) ?? DEFAULT_COUNTRY;
}

const maxLength = (c: Country) => Math.max(...c.lengths);

// ─────────────────────────────────────────────────────────────────────────────
// Somali network detection
// ─────────────────────────────────────────────────────────────────────────────

export interface SomaliNetwork {
  /** Mobile network operator, e.g. `Hormuud`. */
  name: string;
  /** Region the operator predominantly serves — the useful half for an agent. */
  region: string;
}

/**
 * Somali mobile prefixes, by the two leading subscriber digits.
 *
 * This is a *display hint only*. It tells an agent which network a number sits
 * on before they send anything, which is the difference between "the prompt
 * never arrived" and "that is a Somaliland number". It deliberately does not
 * claim anything about which payment rail will work — that is the backend's
 * call, and guessing it here would put a confident wrong answer in front of an
 * operator. Unrecognised prefixes return `null` rather than a guess.
 */
const SOMALI_NETWORKS: Readonly<Record<string, SomaliNetwork>> = {
  '61': { name: 'Hormuud', region: 'South & Central' },
  '68': { name: 'Hormuud', region: 'South & Central' },
  '77': { name: 'Hormuud', region: 'South & Central' },
  '63': { name: 'Telesom', region: 'Somaliland' },
  '65': { name: 'Telesom', region: 'Somaliland' },
  '62': { name: 'Somtel', region: 'Somaliland & Puntland' },
  '66': { name: 'Somtel', region: 'Somaliland & Puntland' },
  '90': { name: 'Golis', region: 'Puntland' },
  '69': { name: 'Golis', region: 'Puntland' },
  '79': { name: 'NationLink', region: 'South & Central' },
  '71': { name: 'Amtel', region: 'South & Central' },
};

/**
 * Identify the network behind a Somali subscriber number, or `null` when the
 * prefix is unallocated, unknown to this build, or the number is not Somali.
 */
export function detectSomaliNetwork(
  national: string,
  iso2: string = DEFAULT_COUNTRY_ISO2
): SomaliNetwork | null {
  if (iso2 !== 'SO' || national.length < 2) return null;
  return SOMALI_NETWORKS[national.slice(0, 2)] ?? null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Parsing
// ─────────────────────────────────────────────────────────────────────────────

export interface ParsedPhone {
  /** Country the number resolved against — never null; falls back to the default. */
  country: Country;
  /** Subscriber digits: no country code, no trunk `0`. May be partial. */
  national: string;
  /** `+252612345678`, or `null` while the number is incomplete. */
  e164: string | null;
  /** `252612345678` — same as `e164` without the `+`, or `null`. */
  digits: string | null;
  /** Grouped for display, e.g. `61 234 5678`. Formats partial input too. */
  formatted: string;
  /** A complete, plausible number for its country. */
  isValid: boolean;
  /** Something was typed, but it is not a complete number yet. */
  isPartial: boolean;
  /** Network hint for Somali numbers; `null` everywhere else. */
  network: SomaliNetwork | null;
}

/** Group subscriber digits per the country's pattern, tolerating partial input. */
export function formatNational(national: string, country: Country): string {
  if (!national) return '';
  const out: string[] = [];
  let i = 0;
  for (const size of country.groups) {
    if (i >= national.length) break;
    out.push(national.slice(i, i + size));
    i += size;
  }
  // Anything past the known groups (over-typed, or a longer variant) trails as-is
  // rather than being dropped — silently swallowing digits is how you lose one.
  if (i < national.length) out.push(national.slice(i));
  return out.join(' ');
}

/**
 * Split raw input into a country and its subscriber digits.
 *
 * The rules, in the order they are tried:
 *   1. An explicit international prefix (`+` or `00`) is authoritative — the
 *      longest matching dial code wins and the country chip follows the paste.
 *   2. Otherwise the digits are read against `fallbackIso2`. A leading trunk
 *      `0` is dropped when doing so yields a valid length.
 *   3. A bare number that *starts with* the fallback country's own dial code is
 *      only treated as international when the remainder is itself a valid
 *      length, or when the string is too long to be a subscriber number. That
 *      guard is what stops a US `1234567890` losing its leading `1`.
 */
export function parsePhone(
  raw: string | null | undefined,
  fallbackIso2: string = DEFAULT_COUNTRY_ISO2
): ParsedPhone {
  const fallback = getCountry(fallbackIso2);
  const text = (raw ?? '').trim();

  const hadIntlPrefix = text.startsWith('+') || /^00\d/.test(text);
  let digits = text.replace(/\D/g, '');
  if (!text.startsWith('+') && digits.startsWith('00')) digits = digits.slice(2);

  let country = fallback;
  let national = digits;

  if (hadIntlPrefix) {
    const match = BY_DIAL_LENGTH.find((c) => digits.startsWith(c.dialCode));
    if (match) {
      country = match;
      national = digits.slice(match.dialCode.length);
    }
  } else if (digits.startsWith(fallback.dialCode)) {
    const rest = digits.slice(fallback.dialCode.length);
    if (fallback.lengths.includes(rest.length) || digits.length > maxLength(fallback)) {
      national = rest;
    }
  }

  // National trunk prefix — only stripped when it actually helps, so a country
  // whose subscriber numbers legitimately begin with 0 is left alone.
  if (
    national.startsWith('0') &&
    !country.lengths.includes(national.length) &&
    country.lengths.includes(national.length - 1)
  ) {
    national = national.slice(1);
  }

  const isValid =
    country.lengths.includes(national.length) &&
    (country.mobileLeading?.test(national) ?? true);

  const e164 = isValid ? `+${country.dialCode}${national}` : null;

  return {
    country,
    national,
    e164,
    digits: e164 ? e164.slice(1) : null,
    formatted: formatNational(national, country),
    isValid,
    isPartial: national.length > 0 && !isValid,
    network: detectSomaliNetwork(national, country.iso2),
  };
}

/**
 * Canonical `+252612345678`, or `null` when the input is not a complete number.
 * The one-liner most call sites want.
 */
export function toE164(
  raw: string | null | undefined,
  fallbackIso2: string = DEFAULT_COUNTRY_ISO2
): string | null {
  return parsePhone(raw, fallbackIso2).e164;
}

/**
 * Normalize free-text agent input into a digits-only Somali MSISDN
 * (`252XXXXXXXXX`), or `null` when the number is not yet a complete, plausible
 * Somali mobile number.
 *
 * Returning `null` for partial input is what gives the ZAAD auto-lookup its
 * natural debounce: the query only enables once a full number has been typed.
 */
export function toSomaliMsisdn(raw: string | null | undefined): string | null {
  const parsed = parsePhone(raw, 'SO');
  return parsed.country.iso2 === 'SO' ? parsed.digits : null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Field state machine
//
// Kept here, out of the component, because this is where the awkward cases
// live — a lone `+` that has no country yet, a paste that changes country
// mid-edit — and none of them are testable once they are tangled up in React
// state and a caret.
// ─────────────────────────────────────────────────────────────────────────────

export interface PhoneFieldState {
  /** Country currently shown in the chip. */
  iso2: string;
  /** Subscriber digits held by the field. */
  national: string;
  /**
   * Literal `+…` text to display while a country code is still being typed,
   * or `null` when the field is showing a normal formatted number.
   *
   * Without this a typed `+` would vanish on the next render: a lone `+`
   * carries no digits, so it formats to the empty string and the agent watches
   * their keystroke disappear.
   */
  intlDraft: string | null;
}

/**
 * Fold raw field text into the next field state.
 *
 * `currentIso2` is the country the chip is on, used as the fallback for input
 * that does not name one. Input that *does* — a pasted `+254…`, a typed `00…`
 * — retargets the chip, which is what makes the picker optional rather than
 * mandatory.
 */
export function applyPhoneInput(
  raw: string,
  currentIso2: string = DEFAULT_COUNTRY_ISO2
): PhoneFieldState {
  // `00` is the same intent as `+`; normalising keeps one code path.
  //
  // Matched without requiring a digit after it, deliberately. `parsePhone`
  // strips a leading `00` as an international prefix whether or not anything
  // follows, so waiting for the third character means the field would show
  // `0`, then blank on the second `0`, then start over — the two keystrokes
  // silently swallowed.
  const normalized = raw.replace(/^00/, '+');

  if (normalized.startsWith('+')) {
    const draft = `+${normalized.slice(1).replace(/\D/g, '')}`;
    const parsed = parsePhone(draft, currentIso2);
    // Commit to a real country only once the prefix has actually matched one
    // *and* a subscriber digit follows it. Until then the draft stays on
    // screen exactly as typed.
    const resolved =
      parsed.national.length > 0 &&
      draft.startsWith(`+${parsed.country.dialCode}`);

    return resolved
      ? { iso2: parsed.country.iso2, national: parsed.national, intlDraft: null }
      : { iso2: currentIso2, national: '', intlDraft: draft };
  }

  const parsed = parsePhone(raw, currentIso2);
  return {
    iso2: parsed.country.iso2,
    national: parsed.national,
    intlDraft: null,
  };
}

/**
 * The value a field in this state hands to its parent.
 *
 * A complete number is emitted in `format`. An incomplete one is always
 * emitted as `+<dial><digits>` regardless of `format`, for two reasons: the
 * parent's own validation then fails on it naturally, and the leading `+`
 * makes it parse back unambiguously so the value round-trips through the
 * field without losing digits.
 */
export function emitPhoneValue(
  state: PhoneFieldState,
  format: 'e164' | 'digits' = 'e164'
): string {
  if (!state.national) return '';
  const country = getCountry(state.iso2);
  const parsed = parsePhone(`+${country.dialCode}${state.national}`, state.iso2);
  if (!parsed.isValid) return `+${country.dialCode}${state.national}`;
  return format === 'digits' ? parsed.digits! : parsed.e164!;
}

/** Only digits and the punctuation people put between them. */
const LOOKS_LIKE_A_NUMBER = /^[\d\s+()./-]+$/;

/**
 * Turn a free-text search term into one that matches a phone column holding
 * mixed formats.
 *
 * The list endpoints match `%term%` against a `phone` column that accumulated
 * `612345678`, `0612345678` and `+252612345678` over time. The subscriber
 * digits are a substring of every one of those spellings, so reducing a
 * phone-shaped term to its national part is what makes a search for a number
 * find the customer regardless of how they were first registered.
 *
 * Anything that is not phone-shaped — a name, a UUID prefix — is returned
 * untouched, so this is safe to drop in front of a shared search box.
 */
export function phoneSearchTerm(
  term: string | null | undefined,
  fallbackIso2: string = DEFAULT_COUNTRY_ISO2
): string {
  const text = (term ?? '').trim();
  if (!text || !LOOKS_LIKE_A_NUMBER.test(text)) return text;
  // Short strings are far more likely to be an id prefix than a phone number.
  if (text.replace(/\D/g, '').length < 7) return text;
  const parsed = parsePhone(text, fallbackIso2);
  return parsed.national || text;
}

/**
 * Render any stored number for display — `+252 61 234 5678`.
 *
 * Falls back to the raw string when it cannot be parsed, because a number the
 * portal does not understand is still a number the operator needs to read.
 */
export function formatPhoneDisplay(
  raw: string | null | undefined,
  fallbackIso2: string = DEFAULT_COUNTRY_ISO2
): string {
  if (!raw) return '';
  const parsed = parsePhone(raw, fallbackIso2);
  if (!parsed.isValid) return raw;
  return `+${parsed.country.dialCode} ${parsed.formatted}`;
}
